import { gql } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client/react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import GroupIcon from '@mui/icons-material/Group';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card, { CardProps } from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import { useNavigate } from '@tanstack/react-location';
import { DateTime } from 'luxon';
import formatMoney from '../../core/formatMoney';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import CheckinChecklist from '../../shared-ui/component/CheckinChecklist';
import CheckoutChecklist from '../../shared-ui/component/CheckoutChecklist';
import { HomeQueryResultBooking, homeQuery } from '../queries';

const dayDiff = (date: string) =>
    Math.ceil(
        DateTime.fromFormat(date, 'yyyy-MM-dd').diffNow('days').toObject()
            .days ?? 0
    );

const formatRelative = (date: string) => {
    const diff = dayDiff(date);
    if (!diff) {
        return 'today';
    }

    if (diff < 0) {
        return `${-diff} days ago`;
    }

    return `${diff} days`;
};

const formatTimeDiff = (booking: {
    checkin: string;
    checkout: string;
    checkinTime: string;
    checkoutTime: string;
}) => {
    const checkoutDiff = dayDiff(booking.checkout);
    const checkinDiff = dayDiff(booking.checkin);

    if (checkoutDiff === 1) {
        return `checkout tomorrow ${booking.checkoutTime}`;
    }

    if (checkoutDiff < 0) {
        return `${formatRelative(booking.checkout)}`;
    }

    if (checkinDiff < 0) {
        return `Checkout in ${formatRelative(booking.checkout)} at ${
            booking.checkoutTime
        }`;
    }

    if (checkinDiff === 1) {
        return `tomorrow at ${booking.checkinTime}`;
    }

    return checkinDiff < 10
        ? `${formatRelative(booking.checkin)} at ${booking.checkinTime}`
        : formatRelative(booking.checkin);
};

interface Props {
    booking: HomeQueryResultBooking;
}

const BookingCardRoot = styled(Card, {
    shouldForwardProp: propName =>
        propName !== 'active' && propName !== 'urgent' && propName !== 'todo',
})<
    CardProps & {
        urgent?: boolean;
        active?: boolean;
        todo?: boolean;
    }
>(({ theme, urgent, active, todo }) => {
    let backgroundColor = '#0f0f23';
    let borderColor = '#374151';
    let accentColor = '#6366f1';

    if (active) {
        backgroundColor = 'rgba(99, 102, 241, 0.15)';
        borderColor = '#6366f1';
        accentColor = '#818cf8';
    }

    if (todo) {
        backgroundColor = 'rgba(245, 158, 11, 0.15)';
        borderColor = '#f59e0b';
        accentColor = '#fbbf24';
    }

    if (urgent) {
        backgroundColor = 'rgba(239, 68, 68, 0.18)';
        borderColor = '#ef4444';
        accentColor = '#f87171';
    }

    return {
        display: 'grid',
        gridTemplateColumns: '1.2fr 1fr auto',
        gap: '1.5rem',
        marginBottom: '0.75rem',
        padding: '1.25rem 1.5rem',
        backgroundColor,
        borderRadius: 0,
        border: `3px solid ${borderColor}`,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
            transform: 'translateX(4px)',
            borderColor: accentColor,
            boxShadow: `4px 4px 0 ${accentColor}`,
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
            gap: '1rem',
            padding: '1rem',
        },
    };
});

const Label = styled(Box)({
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: '#6366f1',
    marginBottom: '0.25rem',
});

const InfoChip = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.875rem',
    color: 'rgba(255, 255, 255, 0.85)',
    '& svg': {
        fontSize: '1rem',
        color: '#6366f1',
    },
});

const BookingCardSkeleton = () => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr auto',
            gap: '1.5rem',
            mb: '0.75rem',
            p: '1.25rem 1.5rem',
            backgroundColor: '#0f0f23',
            border: '3px solid #374151',
            '@media (max-width: 600px)': {
                gridTemplateColumns: '1fr',
                gap: '1rem',
                p: '1rem',
            },
        }}
    >
        <Box>
            <Skeleton variant="text" width={140} height={28} sx={{ bgcolor: 'rgba(99, 102, 241, 0.3)' }} />
            <Skeleton variant="text" width={100} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            <Skeleton variant="text" width={80} height={20} sx={{ bgcolor: 'rgba(245, 158, 11, 0.3)' }} />
        </Box>
        <Box>
            <Skeleton variant="text" width={160} height={24} sx={{ bgcolor: 'rgba(99, 102, 241, 0.2)' }} />
            <Skeleton variant="rectangular" width={100} height={28} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mt: 1 }} />
        </Box>
        <Box sx={{ textAlign: 'right', '@media (max-width: 600px)': { textAlign: 'left' } }}>
            <Skeleton variant="text" width={80} height={28} sx={{ bgcolor: 'rgba(34, 197, 94, 0.3)', ml: 'auto', '@media (max-width: 600px)': { ml: 0 } }} />
        </Box>
    </Box>
);

const BookingCard = ({ booking }: Props) => {
    const navigate = useNavigate();
    const propertyId = usePropertyId();

    const client = useApolloClient();

    const [updateBookingStatus, { loading: updateLoading }] = useMutation(
        gql`
            mutation updateBookingStatus($bookingId: Int!, $status: String!) {
                updateBookingStatus(bookingId: $bookingId, status: $status)
            }
        `,
        {
            variables: {
                bookingId: booking.id,
                status: 'complete',
            },
            refetchQueries: [
                {
                    query: homeQuery,
                },
            ],
        }
    );

    const checkinDiff = dayDiff(booking.checkin);
    const checkoutDiff = dayDiff(booking.checkout);

    if (updateLoading) {
        return <BookingCardSkeleton />;
    }

    const guestDataPercent =
        booking.guests.length > 0
            ? Math.round(
                  (booking.guests.reduce((a, b) => a + (b.valid ? 1 : 0), 0) /
                      booking.guests.length) *
                      100
              )
            : 0;
    const isGuestDataComplete = guestDataPercent === 100;

    const nights = DateTime.fromFormat(booking.checkout, 'yyyy-MM-dd').diff(
        DateTime.fromFormat(booking.checkin, 'yyyy-MM-dd'),
        'days'
    ).days;

    const totalGuests =
        booking.adultCount + booking.childCount + booking.infantCount;

    const handleCardClick = () => {
        navigate({
            to: propertyId
                ? `/${propertyId}/booking/${booking.id}`
                : `/booking/${booking.id}`,
        });
    };

    return (
        <BookingCardRoot
            onClick={handleCardClick}
            urgent={
                (checkinDiff < 11 && !booking.detailsRequested) ||
                (checkinDiff < 3 && !booking.hasInstructions) ||
                (checkoutDiff < -5 && !booking.invoiceId)
            }
            active={
                (checkinDiff < 0 && checkoutDiff > 0) ||
                (checkinDiff < 7 && !booking.detailsRequested)
            }
            todo={checkoutDiff < -1 && !booking.invoiceId}
        >
            {/* Guest Info Column */}
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 0.75,
                    }}
                >
                    {booking.countryCode && (
                        <img
                            src={`flags/${booking.countryCode.toLowerCase()}.png`}
                            alt={booking.countryCode}
                            style={{
                                width: 24,
                                height: 16,
                                border: '2px solid rgba(255,255,255,0.15)',
                            }}
                        />
                    )}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 800,
                            fontSize: '1.25rem',
                            letterSpacing: '0.02em',
                            color: '#fff',
                        }}
                    >
                        {booking.guestName}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        mb: 0.75,
                    }}
                >
                    <InfoChip>
                        <GroupIcon />
                        <span>{totalGuests} guests</span>
                    </InfoChip>
                    <InfoChip>
                        <NightsStayIcon />
                        <span>{nights} nights</span>
                    </InfoChip>
                </Box>
                <Box
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: '#f59e0b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                    }}
                >
                    <CalendarTodayIcon sx={{ fontSize: '1rem' }} />
                    {formatTimeDiff(booking)}
                </Box>
            </Box>

            {/* Schedule & Status Column */}
            <Box>
                <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
                    <Box>
                        <Label>Check-in</Label>
                        <Box
                            sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                color: '#fff',
                            }}
                        >
                            {DateTime.fromSQL(
                                `${booking.checkin} ${booking.checkinTime}`
                            ).toFormat('ccc HH:mm, LLL dd')}
                        </Box>
                    </Box>
                    <Box>
                        <Label>Check-out</Label>
                        <Box
                            sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                color: '#fff',
                            }}
                        >
                            {DateTime.fromSQL(
                                `${booking.checkout} ${booking.checkoutTime}`
                            ).toFormat('ccc HH:mm, LLL dd')}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                        {checkinDiff > 0 && checkinDiff < 10 && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    py: 0.5,
                                    border: '2px solid',
                                    borderColor: isGuestDataComplete
                                        ? 'success.main'
                                        : 'warning.main',
                                    backgroundColor: isGuestDataComplete
                                        ? 'rgba(34, 197, 94, 0.12)'
                                        : 'rgba(245, 158, 11, 0.12)',
                                    fontWeight: 700,
                                    fontSize: '0.8125rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    color: isGuestDataComplete
                                        ? 'success.main'
                                        : 'warning.main',
                                }}
                            >
                                {guestDataPercent}% data
                            </Box>
                        )}
                        {booking.hasInstructions && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1.5,
                                    py: 0.5,
                                    border: '2px solid',
                                    borderColor: booking.instructionDownloadCount > 0
                                        ? 'success.main'
                                        : '#374151',
                                    backgroundColor: booking.instructionDownloadCount > 0
                                        ? 'rgba(34, 197, 94, 0.12)'
                                        : 'transparent',
                                    fontWeight: 700,
                                    fontSize: '0.8125rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    color: booking.instructionDownloadCount > 0
                                        ? 'success.main'
                                        : '#9ca3af',
                                }}
                            >
                                <DownloadIcon sx={{ fontSize: '0.875rem' }} />
                                {booking.instructionDownloadCount > 0
                                    ? `${booking.instructionDownloadCount}x`
                                    : 'Not accessed'}
                            </Box>
                        )}
                        {booking.bookingShouldBeComplete && (
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                sx={{
                                    borderRadius: 0,
                                    border: '2px solid',
                                    borderColor: 'secondary.main',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    px: 2,
                                    py: 0.5,
                                    fontSize: '0.8125rem',
                                    minHeight: 0,
                                }}
                                onClick={e => {
                                    e.stopPropagation();
                                    updateBookingStatus()
                                        .then(async () => {
                                            await client.clearStore();
                                            await client.refetchObservableQueries();
                                        })
                                        .catch(error => {
                                            throw error;
                                        });
                                }}
                            >
                                Complete
                            </Button>
                        )}
                    </Box>
                    {(booking.actionRequired ||
                        booking.urgentActionRequired) && (
                        <Box
                            onClick={e => e.stopPropagation()}
                            sx={{ display: 'flex', gap: 2 }}
                        >
                            <CheckinChecklist bookingId={booking.id} />
                            <CheckoutChecklist bookingId={booking.id} />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Revenue Column */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    minWidth: 100,
                    borderLeft: '2px solid #374151',
                    pl: 2,
                    ml: 1,
                    '@media (max-width: 600px)': {
                        borderLeft: 'none',
                        borderTop: '2px solid #374151',
                        pl: 0,
                        ml: 0,
                        pt: 1,
                        flexDirection: 'row',
                        alignItems: 'center',
                    },
                }}
            >
                <Box>
                    <Label sx={{ textAlign: 'right' }}>Revenue</Label>
                    <Box
                        sx={{
                            color: booking.revenue
                                ? '#22c55e'
                                : '#ef4444',
                            fontWeight: 900,
                            fontSize: '1.5rem',
                            letterSpacing: '-0.01em',
                            fontVariantNumeric: 'tabular-nums',
                            textAlign: 'right',
                        }}
                    >
                        {formatMoney(booking.revenue, booking.currency)}
                    </Box>
                </Box>
            </Box>
        </BookingCardRoot>
    );
};

export default BookingCard;
